/**
 * Diagram Viewer (issue #1077, epic #1002 sub-issue 6/7)
 *
 * Read-only React Flow render of a Service's `Diagram.content` — the System
 * Context shape produced by `DiagramGenerationService` (issue #1075) and
 * read via `Query.diagramsForEntity` (issue #1074). Follows
 * `DependencyGraph.tsx`'s node/edge-building pattern, but deliberately ships
 * no editing affordance: nodes/edges are passed to `<ReactFlow>` as static
 * arrays with `nodesDraggable`/`nodesConnectable`/`elementsSelectable` all
 * `false` and no `onNodesChange`/`onEdgesChange` handlers, so there is no
 * local state a drag could mutate, let alone persist. The drag-and-drop
 * *editor* stays deferred — see
 * documents/specs/future-enhancements-wishlist.md §5.3.
 */

import { useMemo, useState } from 'react';
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  MarkerType,
  type Edge,
  type Node,
} from 'reactflow';
import 'reactflow/dist/style.css';
import type { Diagram } from '../../graphql/types';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import { c4SystemContextContentSchema, type C4SystemContextContent } from './diagramContentSchema';
import './DiagramViewer.css';

export interface DiagramViewerProps {
  /** Every diagram documenting the current entity (`Service.diagrams` / `diagramsForEntity`). */
  diagrams: Diagram[];
  loading?: boolean;
  error?: string | null;
}

/** Themed node fill by role — mirrors DependencyGraph.tsx's token-only convention (issue #653). */
function getNodeColor(kind: 'system' | 'person' | 'Service' | 'Technology'): string {
  switch (kind) {
    case 'system':
      return 'var(--forge-theme-primary, #2196F3)';
    case 'person':
      return 'var(--forge-theme-warning, #FF9800)';
    case 'Service':
      return 'var(--forge-theme-success, #4CAF50)';
    /* c8 ignore next 2 — entityType is typed to 'Service' | 'Technology' by
       the schema union; this default only guards malformed runtime data. */
    default:
      return 'var(--forge-theme-tertiary, #9C27B0)';
  }
}

function getEdgeColor(direction: 'outbound' | 'inbound'): string {
  return direction === 'outbound'
    ? 'var(--forge-theme-primary, #2196F3)'
    : 'var(--forge-theme-outline-high, #9E9E9E)';
}

function nodeLabel(name: string, subtitle: string): JSX.Element {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{name}</div>
      <div style={{ fontSize: '10px', color: 'var(--forge-theme-text-medium-inverse, #666)' }}>
        {subtitle}
      </div>
    </div>
  );
}

/**
 * Build React Flow nodes/edges from a validated System Context payload.
 * Edge labels/colors come straight from `relationships[]`'s real
 * `description`/`direction`/`criticality` fields — never string-matched.
 */
function buildGraph(content: C4SystemContextContent): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  nodes.push({
    id: content.system.id,
    type: 'default',
    position: { x: 400, y: 300 },
    draggable: false,
    connectable: false,
    data: { label: nodeLabel(content.system.name, 'System') },
    style: {
      background: getNodeColor('system'),
      color: 'var(--forge-theme-text-high-inverse, #fff)',
      border: '3px solid var(--forge-theme-primary, #2196F3)',
      borderRadius: '8px',
      padding: '12px',
      width: 180,
      fontSize: '12px',
    },
  });

  content.people.forEach((person, index) => {
    const yOffset = (index - content.people.length / 2) * 120;
    nodes.push({
      id: person.id,
      type: 'default',
      position: { x: 50, y: 300 + yOffset },
      draggable: false,
      connectable: false,
      data: { label: nodeLabel(person.name, 'Team') },
      style: {
        background: getNodeColor('person'),
        color: 'var(--forge-theme-text-high-inverse, #fff)',
        border: '2px solid var(--forge-theme-outline-high, #9E9E9E)',
        borderRadius: '6px',
        padding: '10px',
        width: 150,
        fontSize: '11px',
      },
    });
  });

  content.externalSystems.forEach((system, index) => {
    const yOffset = (index - content.externalSystems.length / 2) * 120;
    nodes.push({
      id: system.id,
      type: 'default',
      position: { x: 750, y: 300 + yOffset },
      draggable: false,
      connectable: false,
      data: { label: nodeLabel(system.name, system.entityType) },
      style: {
        background: getNodeColor(system.entityType),
        color: 'var(--forge-theme-text-high-inverse, #fff)',
        border: '2px solid var(--forge-theme-outline-high, #9E9E9E)',
        borderRadius: '6px',
        padding: '10px',
        width: 150,
        fontSize: '11px',
      },
    });
  });

  content.relationships.forEach((rel, index) => {
    edges.push({
      id: `${rel.fromId}-${rel.toId}-${index}`,
      source: rel.fromId,
      target: rel.toId,
      label: rel.description,
      type: 'smoothstep',
      style: {
        stroke: getEdgeColor(rel.direction),
        strokeWidth: rel.direction === 'outbound' ? 2 : 1,
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: getEdgeColor(rel.direction),
      },
      labelStyle: {
        fontSize: '10px',
        fill: 'var(--forge-theme-text-medium, #666)',
      },
      labelBgStyle: {
        fill: 'var(--forge-theme-surface, #fff)',
      },
    });
  });

  return { nodes, edges };
}

/**
 * DiagramViewer — read-only. Owns its own loading/error/empty states, like
 * DependencyGraph, so callers just pass through GraphQL query results.
 */
export default function DiagramViewer({
  diagrams,
  loading = false,
  error = null,
}: DiagramViewerProps): JSX.Element {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selectedDiagram = useMemo(() => {
    if (diagrams.length === 0) return null;
    return diagrams.find((d) => d.entityId === selectedId) ?? diagrams[0];
  }, [diagrams, selectedId]);

  const parsedContent = useMemo(() => {
    if (!selectedDiagram) return null;
    const result = c4SystemContextContentSchema.safeParse(selectedDiagram.content);
    return result.success ? result.data : null;
  }, [selectedDiagram]);

  const { nodes, edges } = useMemo(() => {
    if (!parsedContent) return { nodes: [] as Node[], edges: [] as Edge[] };
    return buildGraph(parsedContent);
  }, [parsedContent]);

  if (loading) {
    return (
      <div className="diagram-viewer-placeholder">
        <LoadingSpinner message="Loading diagrams..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="diagram-viewer-placeholder">
        <EmptyState title="Failed to load diagrams" description={error} icon="error" />
      </div>
    );
  }

  if (diagrams.length === 0) {
    return (
      <div className="diagram-viewer-placeholder">
        <EmptyState
          title="No diagrams yet"
          description="This service has no generated diagrams yet."
          icon="account_tree"
        />
      </div>
    );
  }

  if (!selectedDiagram || !parsedContent) {
    return (
      <div className="diagram-viewer-placeholder">
        <EmptyState
          title="Cannot render this diagram"
          description="This diagram's content is not in a viewer-supported shape yet."
          icon="warning"
        />
      </div>
    );
  }

  return (
    <div className="diagram-viewer">
      {diagrams.length > 1 && (
        <div className="diagram-viewer-picker">
          <label htmlFor="diagram-viewer-select" className="diagram-viewer-picker-label">
            Diagram
          </label>
          <select
            id="diagram-viewer-select"
            className="diagram-viewer-picker-select"
            value={selectedDiagram.entityId}
            onChange={(e) => setSelectedId(e.target.value)}
            aria-label="Select diagram"
          >
            {diagrams.map((d) => (
              <option key={d.entityId} value={d.entityId}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="diagram-viewer-canvas">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnScroll
          zoomOnScroll
          fitView
          attributionPosition="bottom-left"
        >
          <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
    </div>
  );
}
